import { useState, useMemo } from 'react'
import { 
  FunnelIcon, 
  XMarkIcon, 
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  UserIcon,
  TagIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { useAppSelector } from '../hooks/redux'
import type { BoardFilterState, Issue, IssuePriority } from '@/types'
import { calculateFilterStats, hasActiveFilters } from '../utils/filterUtils'

interface BoardFiltersProps {
  projectId: string
  filters: BoardFilterState
  onFiltersChange: (filters: BoardFilterState) => void
  issues: Issue[]
  filteredCount: number
  totalCount: number
}

const BoardFilters = ({
  filters,
  onFiltersChange,
  issues,
  filteredCount,
  totalCount
}: BoardFiltersProps) => {
  const { allLabels } = useAppSelector(state => state.label)
  const { currentProject } = useAppSelector(state => state.project)
  const { user } = useAppSelector(state => state.auth)
  
  const [showAllLabels, setShowAllLabels] = useState(false)
  
  // 計算篩選統計
  const stats = useMemo(() => calculateFilterStats(issues), [issues])
  
  // 獲取專案成員列表
  const projectMembers = useMemo(() => {
    return currentProject?.members?.map(member => member.user) || []
  }, [currentProject])
  
  // 更新篩選狀態的輔助函數
  const updateFilters = (updates: Partial<BoardFilterState>) => {
    onFiltersChange({ ...filters, ...updates })
  }
  
  // 標籤篩選處理
  const handleLabelToggle = (labelId: string) => {
    const newLabels = filters.labels.includes(labelId)
      ? filters.labels.filter(id => id !== labelId)
      : [...filters.labels, labelId]
    updateFilters({ labels: newLabels })
  }
  
  // 指派者篩選處理
  const handleAssigneeToggle = (userId: string) => {
    const newAssignees = filters.assignees.includes(userId)
      ? filters.assignees.filter(id => id !== userId)
      : [...filters.assignees, userId]
    updateFilters({ assignees: newAssignees })
  }
  
  // 優先級篩選處理
  const handlePriorityToggle = (priority: IssuePriority) => {
    const newPriorities = filters.priorities.includes(priority)
      ? filters.priorities.filter(p => p !== priority)
      : [...filters.priorities, priority]
    updateFilters({ priorities: newPriorities })
  }
  
  // 快速篩選處理
  const handleQuickFilter = (type: 'myIssues' | 'unassigned' | 'createdByMe') => {
    switch (type) {
      case 'myIssues':
        updateFilters({ showMyIssues: !filters.showMyIssues })
        break
      case 'unassigned':
        updateFilters({ showUnassigned: !filters.showUnassigned })
        break
      case 'createdByMe':
        updateFilters({ showCreatedByMe: !filters.showCreatedByMe })
        break
    }
  }
  
  // 清除所有篩選
  const clearAllFilters = () => {
    updateFilters({
      labels: [],
      labelLogic: 'OR',
      assignees: [],
      showMyIssues: false,
      showUnassigned: false,
      showCreatedByMe: false,
      priorities: [],
      dueDateFilter: { type: null },
      searchQuery: ''
    })
  }
  
  const activeFiltersCount = hasActiveFilters(filters)
  const displayLabels = showAllLabels ? allLabels : allLabels.slice(0, 8)
  
  return (
    <div className="mb-4 space-y-3">
      {/* 頂部控制列 */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center space-x-2">
          {/* 搜尋框 */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜尋議題..."
              value={filters.searchQuery}
              onChange={(e) => updateFilters({ searchQuery: e.target.value })}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* 快速篩選按鈕 */}
          <button
            onClick={() => handleQuickFilter('myIssues')}
            className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              filters.showMyIssues
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <UserIcon className="h-4 w-4" />
            <span>我的議題</span>
            {stats.assigneeCounts.get(user?.id || '') && (
              <span className="bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                {stats.assigneeCounts.get(user?.id || '')}
              </span>
            )}
          </button>
          
          <button
            onClick={() => handleQuickFilter('unassigned')}
            className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              filters.showUnassigned
                ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <ExclamationTriangleIcon className="h-4 w-4" />
            <span>未指派</span>
            {stats.assigneeCounts.get('unassigned') && (
              <span className="bg-yellow-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                {stats.assigneeCounts.get('unassigned')}
              </span>
            )}
          </button>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* 篩選器展開按鈕 */}
          <button
            onClick={() => updateFilters({ isExpanded: !filters.isExpanded })}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              filters.isExpanded || activeFiltersCount
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <FunnelIcon className="h-4 w-4" />
            <span>篩選器</span>
            {activeFiltersCount && (
              <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                篩選中
              </span>
            )}
            {filters.isExpanded ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
          
          {/* 結果統計 */}
          <div className="text-sm text-gray-600">
            顯示 {filteredCount} / {totalCount} 個議題
          </div>
        </div>
      </div>
      
      {/* 已套用的篩選標籤 */}
      {activeFiltersCount && (
        <div className="flex items-center space-x-2 flex-wrap gap-1">
          <span className="text-sm text-gray-500">已套用:</span>
          
          {/* 標籤篩選標籤 */}
          {filters.labels.map(labelId => {
            const label = allLabels.find(l => l.id === labelId)
            if (!label) return null
            return (
              <span
                key={labelId}
                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700"
              >
                <TagIcon className="h-3 w-3 mr-1" />
                {label.name}
                <button
                  onClick={() => handleLabelToggle(labelId)}
                  className="ml-1 hover:text-gray-900"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )
          })}
          
          {/* 優先級篩選標籤 */}
          {filters.priorities.map(priority => (
            <span
              key={priority}
              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700"
            >
              優先級:{priority}
              <button
                onClick={() => handlePriorityToggle(priority)}
                className="ml-1 hover:text-gray-900"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          
          {/* 快速篩選標籤 */}
          {filters.showMyIssues && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
              我的議題
              <button
                onClick={() => handleQuickFilter('myIssues')}
                className="ml-1 hover:text-blue-900"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          )}
          
          {filters.showUnassigned && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
              未指派
              <button
                onClick={() => handleQuickFilter('unassigned')}
                className="ml-1 hover:text-yellow-900"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          )}
          
          {/* 清除所有按鈕 */}
          <button
            onClick={clearAllFilters}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 hover:bg-gray-100 rounded"
          >
            清除所有
          </button>
        </div>
      )}
      
      {/* 展開的篩選面板 */}
      {filters.isExpanded && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* 標籤篩選 */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900 flex items-center">
                  <TagIcon className="h-4 w-4 mr-1" />
                  標籤篩選
                </h3>
                <div className="flex items-center space-x-2">
                  <select
                    value={filters.labelLogic}
                    onChange={(e) => updateFilters({ labelLogic: e.target.value as 'AND' | 'OR' })}
                    className="text-xs border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="OR">任一標籤</option>
                    <option value="AND">所有標籤</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {displayLabels.map(label => {
                  const isSelected = filters.labels.includes(label.id)
                  const count = stats.labelCounts.get(label.id) || 0
                  
                  return (
                    <button
                      key={label.id}
                      onClick={() => handleLabelToggle(label.id)}
                      className={`w-full flex items-center justify-between px-2 py-1 rounded text-xs transition-all ${
                        isSelected
                          ? 'ring-2 ring-offset-1 shadow-sm'
                          : 'hover:shadow-sm opacity-75 hover:opacity-100'
                      }`}
                      style={{
                        backgroundColor: isSelected ? label.color : label.color + '40',
                        color: isSelected ? '#fff' : label.color
                      } as React.CSSProperties}
                    >
                      <span>{label.name}</span>
                      <span className="bg-black bg-opacity-20 rounded-full px-1 min-w-[16px] text-center">
                        {count}
                      </span>
                    </button>
                  )
                })}
              </div>
              
              {allLabels.length > 8 && (
                <button
                  onClick={() => setShowAllLabels(!showAllLabels)}
                  className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                >
                  {showAllLabels ? '顯示較少' : `顯示全部 ${allLabels.length} 個標籤`}
                </button>
              )}
            </div>
            
            {/* 指派對象篩選 */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <UserIcon className="h-4 w-4 mr-1" />
                指派對象
              </h3>
              
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {projectMembers.map(member => {
                  const isSelected = filters.assignees.includes(member.id)
                  const count = stats.assigneeCounts.get(member.id) || 0
                  
                  return (
                    <button
                      key={member.id}
                      onClick={() => handleAssigneeToggle(member.id)}
                      className={`w-full flex items-center justify-between px-2 py-1 rounded text-xs transition-all ${
                        isSelected
                          ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span className="flex items-center">
                        <img
                          src={member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&size=16`}
                          alt={member.name}
                          className="w-4 h-4 rounded-full mr-1"
                        />
                        {member.name}
                      </span>
                      <span className="bg-gray-200 rounded-full px-1 min-w-[16px] text-center">
                        {count}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
            
            {/* 優先級篩選 */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                優先級
              </h3>
              
              <div className="space-y-2">
                {(['HIGHEST', 'HIGH', 'MEDIUM', 'LOW', 'LOWEST'] as IssuePriority[]).map(priority => {
                  const isSelected = filters.priorities.includes(priority)
                  const count = stats.priorityCounts.get(priority) || 0
                  
                  const priorityConfig = {
                    HIGHEST: { label: '最高', color: 'text-red-600', bg: 'bg-red-100' },
                    HIGH: { label: '高', color: 'text-orange-600', bg: 'bg-orange-100' },
                    MEDIUM: { label: '中', color: 'text-yellow-600', bg: 'bg-yellow-100' },
                    LOW: { label: '低', color: 'text-green-600', bg: 'bg-green-100' },
                    LOWEST: { label: '最低', color: 'text-gray-600', bg: 'bg-gray-100' }
                  }
                  
                  const config = priorityConfig[priority]
                  
                  return (
                    <button
                      key={priority}
                      onClick={() => handlePriorityToggle(priority)}
                      className={`w-full flex items-center justify-between px-2 py-1 rounded text-xs transition-all ${
                        isSelected
                          ? `${config.bg} ${config.color} ring-2 ring-offset-1`
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span>{config.label}</span>
                      <span className="bg-gray-200 rounded-full px-1 min-w-[16px] text-center">
                        {count}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BoardFilters